/**
 * Groups: departments, teams and tags.
 *
 * One panel for both audiences, because they are the same object. Admins see
 * the office's departments, teams and global tags and can edit them; everyone
 * sees their own personal tags and can edit those. What you may change is
 * decided by the server on every call — this only decides what to draw.
 */

import type { Account, Group, GroupKind } from '@virtual-office/shared';

import * as api from '../api.js';
import type { Store } from '../state.js';
import { $, clear, el } from './dom.js';
import { toast } from './toasts.js';

const KIND_LABELS: Record<GroupKind, string> = {
  department: 'Department',
  team: 'Team',
  tag: 'Tag',
};

export class GroupsPanel {
  private readonly panel = $('groups');
  private readonly body = $('groups-body');
  private groups: Group[] = [];
  private accounts: Account[] = [];

  /** Group whose members are being edited in the dialog, if any. */
  private editing: string | null = null;
  /** Department or team currently narrowing the dialog's list. */
  private filter: string | null = null;

  constructor(private readonly store: Store) {
    $('groups-close').addEventListener('click', () => this.close());
    $('member-dialog-close').addEventListener('click', () => this.closeMembers());
    // Clicking the backdrop closes it, like every other dialog people meet.
    $('member-dialog').addEventListener('click', (event) => {
      if (event.target === $('member-dialog')) this.closeMembers();
    });
    // Escape too. Handled here rather than in the global shortcut handler,
    // which deliberately ignores every key while a modal is open.
    window.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.editing !== null) {
        event.stopPropagation();
        this.closeMembers();
      }
    });
  }

  get isOpen(): boolean {
    return !this.panel.hidden;
  }

  open(): void {
    this.panel.hidden = false;
    void this.load();
  }

  close(): void {
    this.panel.hidden = true;
    this.closeMembers();
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  private get isAdmin(): boolean {
    return this.store.me?.role === 'admin';
  }

  private async load(): Promise<void> {
    clear(this.body);
    this.body.append(el('p', { className: 'empty', text: 'Loading…' }));
    try {
      const result = await api.listGroups();
      this.groups = result.groups;
      this.accounts = result.accounts;
      this.render();
    } catch (error) {
      clear(this.body);
      this.body.append(
        el('p', {
          className: 'error',
          text: error instanceof Error ? error.message : 'Could not load groups.',
        }),
      );
    }
  }

  private render(): void {
    clear(this.body);

    const mine = this.groups.filter((group) => group.ownerId !== null);
    const global = this.groups.filter((group) => group.ownerId === null);

    if (this.isAdmin) {
      this.body.append(el('p', { className: 'option-label', text: 'Office' }));
      this.body.append(this.creationForm(false));
      if (global.length === 0) {
        this.body.append(el('p', { className: 'empty', text: 'No departments or teams yet.' }));
      }
      for (const group of global) this.body.append(this.renderGroup(group, true));
    } else if (global.length > 0) {
      // Members can see what exists so they know what they can mention, but
      // editing is an admin's job and there are no controls to imply otherwise.
      this.body.append(el('p', { className: 'option-label', text: 'Office' }));
      for (const group of global) this.body.append(this.renderGroup(group, false));
    }

    this.body.append(el('p', { className: 'option-label section-break', text: 'Your tags' }));
    this.body.append(
      el('p', {
        className: 'hint-inline',
        text: 'Private to you. Nobody else sees these exist.',
      }),
    );
    this.body.append(this.creationForm(true));
    if (mine.length === 0) {
      this.body.append(el('p', { className: 'empty', text: 'No tags of your own yet.' }));
    }
    for (const group of mine) this.body.append(this.renderGroup(group, true));
  }

  private creationForm(personal: boolean): HTMLElement {
    const name = el('input');
    name.placeholder = personal ? 'my-project' : 'Engineering';
    name.maxLength = 60;

    const kind = el('select', { className: 'admin-role' });
    if (!personal) {
      for (const value of ['department', 'team', 'tag'] as GroupKind[]) {
        const option = el('option', { text: KIND_LABELS[value] });
        option.value = value;
        kind.append(option);
      }
    }

    const submit = el('button', { text: 'Create' });
    submit.type = 'submit';

    const children = personal ? [name, submit] : [name, kind, submit];
    const form = el('form', { className: 'admin-form' }, children);
    if (personal) form.classList.add('two-up');

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void (async () => {
        submit.disabled = true;
        try {
          await api.createGroup({
            name: name.value.trim(),
            kind: personal ? 'tag' : (kind.value as GroupKind),
            scope: personal ? 'personal' : 'global',
          });
          name.value = '';
          await this.load();
        } catch (error) {
          toast(error instanceof Error ? error.message : 'Could not create that.', 'error');
        } finally {
          submit.disabled = false;
        }
      })();
    });
    return form;
  }

  private renderGroup(group: Group, editable: boolean): HTMLElement {
    const card = el('div', { className: 'admin-room' });

    const heading = el('div', { className: 'admin-row-main' }, [
      el('span', { className: 'admin-row-title', text: `@${group.slug}` }),
      el('span', {
        className: 'admin-row-detail',
        text: `${KIND_LABELS[group.kind]} · ${group.memberIds.length} ${
          group.memberIds.length === 1 ? 'person' : 'people'
        }`,
      }),
    ]);

    const head = el('div', { className: 'group-head' }, [heading]);

    if (editable) {
      const toggle = el('button', { className: 'ghost small', text: 'Members' });
      toggle.type = 'button';
      toggle.addEventListener('click', () => this.openMembers(group));

      const remove = el('button', { className: 'ghost small danger', text: 'Delete' });
      remove.type = 'button';
      remove.addEventListener('click', () => {
        void (async () => {
          if (!confirm(`Delete @${group.slug}?`)) return;
          await api.deleteGroup(group.id);
          await this.load();
        })();
      });
      head.append(toggle, remove);
    }

    card.append(head);
    return card;
  }

  /* --- member dialog ------------------------------------------------------ */

  /**
   * Editing membership in a dialog rather than inline.
   *
   * The list is every account in the office, which is far too long to unfold
   * inside a panel that also has to show the other groups. A dialog gets the
   * whole screen and can afford a filter.
   */
  private openMembers(group: Group): void {
    this.editing = group.id;
    this.filter = null;
    $('member-dialog-title').textContent = `@${group.slug}`;
    $('member-dialog').hidden = false;
    this.renderDialog();
  }

  private closeMembers(): void {
    this.editing = null;
    $('member-dialog').hidden = true;
  }

  /** Departments and teams, which are the only things worth filtering by. */
  private filterable(): Group[] {
    return this.groups.filter((g) => g.ownerId === null && g.kind !== 'tag');
  }

  private renderDialog(): void {
    const group = this.groups.find((g) => g.id === this.editing);
    if (!group) return this.closeMembers();

    const count = group.memberIds.length;
    $('member-dialog-hint').textContent =
      count === 0 ? 'Nobody yet.' : `${count} ${count === 1 ? 'person' : 'people'}`;

    // Filter chips: all, then one per department and team.
    const filters = $('member-filters');
    clear(filters);
    const addFilter = (label: string, value: string | null) => {
      const chip = el('button', { className: 'board-tab', text: label });
      chip.type = 'button';
      if (this.filter === value) chip.classList.add('selected');
      chip.addEventListener('click', () => {
        this.filter = value;
        this.renderDialog();
      });
      filters.append(chip);
    };
    addFilter('Everyone', null);
    for (const other of this.filterable()) addFilter(other.name, other.id);

    // Only people in the chosen department or team.
    const scope = this.filter ? this.groups.find((g) => g.id === this.filter) : null;
    const people = scope
      ? this.accounts.filter((a) => scope.memberIds.includes(a.id))
      : this.accounts;

    const list = $('member-list');
    clear(list);
    if (people.length === 0) {
      list.append(el('p', { className: 'empty', text: 'Nobody in that group yet.' }));
      return;
    }

    for (const account of people) {
      const inGroup = group.memberIds.includes(account.id);
      const row = el('button', { className: 'member-row' });
      row.type = 'button';
      if (inGroup) row.classList.add('selected');

      row.append(
        el('span', { className: 'member-check', text: inGroup ? '✓' : '' }),
        el('div', { className: 'admin-row-main' }, [
          el('span', { className: 'admin-row-title', text: account.name }),
          el('span', { className: 'admin-row-detail', text: account.email }),
        ]),
      );

      row.addEventListener('click', () => {
        void (async () => {
          row.disabled = true;
          try {
            await api.setGroupMember(group.id, account.id, !inGroup);
            // Reload so the count and every other group stay accurate.
            await this.load();
            this.renderDialog();
          } catch (error) {
            toast(error instanceof Error ? error.message : 'Could not change that.', 'error');
            row.disabled = false;
          }
        })();
      });
      list.append(row);
    }
  }
}
